<template>
    <div v-if="loading?.general" class="loader-units-horizontal">
        <ui-load-indicator :h="70" :stroke="8" bg-color="#DFE5EC" color="#69D296" />
    </div>

    <div v-else class="container-digital-mirror">
        <section class="header-digital-mirror">
            <span class="title">Resumo Geral</span>

            <div class="container-card">
                <ui-product-result-card
                    v-for="(situation, index) in visualization.summary"
                    :key="index"
                    :data="situation"
                />
            </div>
        </section>

        <section class="content-digital-mirror">
            <ui-card-toggle
                v-for="(block, index) in visualization.blocks"
                :key="index"
                slim
                :icon-arrow="false"
                @toggle="toggleHandler($event, block.block)"
            >
                <template #title>
                    <div class="title-value-result">
                        <div class="content-title">
                            <span class="title">{{ block.label }}</span>
                        </div>

                        <section class="container-value-result">
                            <div
                                v-for="(situation, sIndex) in block.summary"
                                :key="sIndex"
                                class="value-result"
                            >
                                <span class="value">{{ situation.value }}</span>

                                <div class="status-result">
                                    <div class="color-result" :style="{ 'background': situation.bg_color }" />

                                    <span class="text-result">
                                        {{ situation.label }}
                                    </span>
                                </div>
                            </div>
                        </section>
                    </div>
                </template>

                <template #content>
                    <section class="container-card-value-result">
                        <div
                            v-for="(unit, uIndex) in block.units"
                            :key="uIndex"
                            :style="{ 'background': unit.bg_color }"
                            class="card-value-result"
                            @click="showUnit(unit)"
                        >
                            <span class="card-value">{{ unit.number }}</span>

                            <span class="card-text">{{ unit.situation_label }}</span>
                        </div>

                    </section>

                    <div class="button-load-more">
                        <ui-button
                            v-if="showUnitsLoader(block.block)"
                            :loading="loading?.blocks[block.block]"
                            design="lg line-primary-alt mw-150"
                            @click="loadBlock(block.block, true)"
                        >
                            Carregar Mais...
                        </ui-button>
                    </div>
                </template>
            </ui-card-toggle>
        </section>
    </div>
</template>

<script>
    import { uiCardToggle } from '@/components/ui/cards'
	import { uiButton } from "@/components/ui/buttons"
	import { uiLoadIndicator } from '@/components/ui'

    import uiProductResultCard from '@/components/ui-facilita/products/product-result-card'

    import { currencyBRL } from '@/filters'

    import { mapGetters, mapActions } from 'vuex'

    export default {
        name: 'ui-products-digital-mirror',

        components: {
            uiProductResultCard,
            uiCardToggle,
            uiButton,
            uiLoadIndicator
        },

        filters: { currencyBRL },

        props: {
            id: {
                type: [String, Number],
                required: true
            }
        },

        data() {
            return {
                unsubscribe: null
            }
        },

        computed: {
            ...mapGetters('product', {
                product: 'getProduct',
                visualization: 'getVisualization',
                visualizationParams: 'getVisualizationParams',
                visualizationPaginations: 'getVisualizationPaginations',
                loading: 'getLoading'
            })
        },

        created() {
            /** Subscreve para uma mutation */
            this.unsubscribe = this.$store.subscribe((mutation) => {
                if (mutation.type === 'product/SET_PRODUCT' &&
                    mutation?.payload?.id &&
                    mutation.payload?.availability?.type === 'vertical') {
                    this.$router.push({
                        name: 'products.unitsVertical',
                        params: { id: mutation.payload.id },
                        replace: true
                    })
                }
            })
        },

        beforeDestroy() {
            this.unsubscribe()
            this.$store.dispatch('product/clearVisualization')
        },

        mounted() {
            this.loadVisualization(this.id)
        },

        methods: {
            ...mapActions('product', [
                'loadVisualization',
                'setBlockPagination'
            ]),
            /**
             * Verifica se determinada quadra ainda possui unidades a serem carregadas
             */
            showUnitsLoader(block) {
                return !(this.visualizationPaginations && this.visualizationPaginations[block]) ||
                    (this.loading?.blocks && this.loading?.blocks[block]) ||
                    (this.visualizationPaginations &&
                        this.visualizationPaginations[block] &&
                        (this.visualizationPaginations[block].current_page !==
                            this.visualizationPaginations[block].last_page))
            },
            /**
             * Seta os parâmetros de filtragem necessários para a visualização e emite o evento para
             * carregar os dados de uma quadra
             */
            loadBlock(value, next = false) {
                // Busca os parâmetros de visualização salvos
                let params = { ...this.visualizationParams }
                let pagination = {
                    ...this.visualizationPaginations && this.visualizationPaginations[value] ||
                    { page: 1, per_page: 50, current_page: 1 }
                }
                params.header = false

                // Verifica se uma nova página de unidades fora solicitada
                if (next) {
                    // Verifica se ainda existem páginas disponíveis para serem carregadas
                    if (pagination.current_page < pagination.last_page) {
                        pagination.page = pagination.current_page + 1
                        pagination.current_page = pagination.page
                        this.setBlockPagination({ block: value, pagination })

                    } else {
                        return
                    }
                }

                params.filter = params.filter?.blocks === value
                    ? {
                        ...params.filter,
                        ...pagination
                    }
                    : { blocks: value, ...pagination }

                this.$store.dispatch('product/setVisualizationParams', params)
                    .then(() => {
                        this.loadVisualization(this.id)
                    })
            },
            /**
             * Event handler que trata o evento de quando uma quadra é selecionada
             */
            toggleHandler(event, block) {
                if (!event) return

                // Busca as unidades já carregadas da quadra que acabou de ser selecionada
                const blockUnits = this.visualization.blocks.find(item => item.block === block)?.units
                // Caso a quadra ainda não tenha nenhuma unidade carregada, realiza o primeiro carregamento
                if (!blockUnits?.length) this.loadBlock(block)
            },
            /**
             * Emite um evento para mostrar os dados da unidade em um modal
             */
            showUnit(unit) {
                let payload = {
                    id: unit.integration_id || unit.id,
                    visualization: 'digitalmirror'
                }
                if (unit.integration_id) payload.availability_id = this.product?.availability_id
                this.$root.$emit('modal-open-units-data', payload)
            }
        }
    }
</script>

<style lang="stylus" scoped>
	@import './styles.styl'
</style>